import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import "aos/dist/aos.css";
import CategorySkeleton from "./CategorySkeleton";

const CategoriesWrapper = styled.div`
  padding: 60px 0;
  background: #fff;

  .section-header {
    text-align: center;
    margin-bottom: 50px;

    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 15px;
      color: #000;
      font-family: "Outfit", sans-serif;
    }
  }

  .carousel-container {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;

    &:hover .carousel-nav {
      opacity: 1;
    }
  }

  .carousel-wrapper {
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #e8e8e8;
      border-radius: 3px;

      &:hover {
        background: #d0d0d0;
      }
    }

    @media (max-width: 768px) {
      overflow: visible;
    }
  }

  .categories-carousel {
    display: flex;
    gap: 25px;
    padding: 20px 0;

    @media (max-width: 768px) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }

    @media (max-width: 576px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;
    }
  }

  .category-card {
    flex: 0 0 calc(20% - 20px);
    min-width: 160px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    height: 240px;
    display: flex;
    align-items: flex-end;
    justify-content: center;

    @media (max-width: 768px) {
      flex: 1;
      height: 200px;
    }

    @media (max-width: 576px) {
      height: 180px;
    }

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0.6) 100%
      );
      z-index: 1;
      transition: all 0.3s ease;
    }

    &:hover {
      &:before {
        background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.2) 0%,
          rgba(0, 0, 0, 0.5) 100%
        );
      }

      .category-image-wrapper {
        transform: scale(1.1);
      }
    }

    .category-image-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-size: cover;
      background-position: center;
      transition: transform 0.3s ease;
      z-index: 0;
    }

    .category-content {
      position: relative;
      z-index: 2;
      text-align: center;
      padding: 20px;
      width: 100%;
    }

    .category-name {
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      margin: 0 0 8px 0;
      transition: color 0.3s ease;
      font-family: "Outfit", sans-serif;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    .category-count {
      font-size: 13px;
      color: #fd8e07;
      margin: 0;
      font-family: "Outfit", sans-serif;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    }
  }

  .carousel-nav {
    position: absolute;
    top: 250px;
    transform: translateY(-50%);
    background: #fff;
    border: 1px solid #e8e8e8;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
    font-size: 16px;
    color: #000;
    opacity: 0;
    pointer-events: auto;

    &:hover {
      background: #f5f5f5;
      border-color: #667eea;
      color: #667eea;
      transform: translateY(-50%) scale(1.15);
    }

    &.prev {
      left: 30px;
    }

    &.next {
      right: 30px;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 1200px) {
    .category-card {
      flex: 0 0 calc(25% - 19px);
      min-width: 140px;
      height: 200px;
    }

    .category-name {
      font-size: 14px;
    }
  }

  @media (max-width: 1024px) {
    .category-card {
      flex: 0 0 calc(33.33% - 17px);
      min-width: 150px;
      height: 220px;
    }

    .category-name {
      font-size: 13px;
    }

    .category-count {
      font-size: 12px;
    }
  }

  @media (max-width: 768px) {
    padding: 40px 0;

    .section-header h2 {
      font-size: 1.8rem;
    }

    .category-name {
      font-size: 14px;
    }

    .category-count {
      font-size: 12px;
    }
  }

  @media (max-width: 576px) {
    .category-name {
      font-size: 12px;
    }

    .category-count {
      font-size: 11px;
    }
  }
`;

const Categories = () => {
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/categories`;
        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }

        const data = await response.json();

        if (data.status && data.data) {
          // Only get top-level categories (parent_id is null or undefined)
          const topLevelCategories = data.data;
          setCategories(topLevelCategories);
        }

        setError(null);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError(err.message);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const checkScrollable = () => {
      if (carouselRef.current) {
        const isScrollableContent =
          carouselRef.current.scrollWidth > carouselRef.current.clientWidth;
        setIsScrollable(isScrollableContent);
      }
    };

    // Check on initial load
    setTimeout(checkScrollable, 100);

    // Check on window resize
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [categories]);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 300;
      if (direction === "left") {
        carouselRef.current.scrollLeft -= scrollAmount;
      } else {
        carouselRef.current.scrollLeft += scrollAmount;
      }
    }
  };

  return (
    <CategoriesWrapper>
      <div className="carousel-container">
        <div className="section-header">
          <h2>Shop By Categories</h2>
        </div>

        {loading && <CategorySkeleton count={6} />}
        {error && (
          <p style={{ textAlign: "center", padding: "40px 0", color: "red" }}>
            Error: {error}
          </p>
        )}

        {!loading && !error && categories.length > 0 && (
          <>
            <div className="carousel-wrapper" ref={carouselRef}>
              <div className="categories-carousel">
                {categories.map((category, index) => {
                  const imageUrl = category.image_url;

                  return (
                    <div
                      key={category.id}
                      className="category-card"
                      // data-aos="fade-up"
                      // data-aos-delay={index * 100}
                      onClick={() =>
                        navigate(`/shop?category=${category.slug}`, {
                          state: { categoryId: category.id },
                        })
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="category-image-wrapper"
                        style={{
                          backgroundImage: `url(${imageUrl})`,
                        }}
                      />
                      <div className="category-content">
                        <h3 className="category-name">{category.name}</h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Navigation Buttons - Only show if carousel is scrollable */}
            {isScrollable && (
              <>
                <div
                  className="carousel-nav prev"
                  onClick={() => scrollCarousel("left")}
                  title="Scroll Left"
                >
                  <LeftOutlined />
                </div>
                <div
                  className="carousel-nav next"
                  onClick={() => scrollCarousel("right")}
                  title="Scroll Right"
                >
                  <RightOutlined />
                </div>
              </>
            )}
          </>
        )}

        {!loading && !error && categories.length === 0 && (
          <p style={{ textAlign: "center", padding: "40px 0" }}>
            No categories found
          </p>
        )}
      </div>
    </CategoriesWrapper>
  );
};

export default Categories;
